<script>
import ClassificationBadge from 'dashboard/routes/dashboard/comments/components/ClassificationBadge.vue';
import MetadataBadge from 'dashboard/routes/dashboard/comments/components/MetadataBadge.vue';
import CustomClassificationBadge from 'dashboard/routes/dashboard/comments/components/CustomClassificationBadge.vue';
import { mapGetters } from 'vuex';
import CustomIcon from 'dashboard/components/ui/CustomIcons.vue';

export default {
  name: 'AITestHistoryDrawer',
  components: {
    ClassificationBadge,
    MetadataBadge,
    CustomClassificationBadge,
    CustomIcon,
  },
  props: {
    showHistory: {
      type: Boolean,
      required: true,
    },
    post: {
      type: Object,
      required: true,
    },
  },
  emits: ['update:showHistory'],
  data() {
    return {
      pagination: {
        perPage: 2,
        currentPage: 1,
      },
      expandedComments: {},
    };
  },
  computed: {
    ...mapGetters({
      testModeHistory: 'comments/getTestModeHistory',
    }),
    hasMore() {
      return (
        this.testModeHistory.pagination.currentPage <
        this.testModeHistory.pagination.totalPages
      );
    },
    commentMetadataType() {
      return metadata => {
        if (metadata?.purchase_intention) {
          return 'PURCHASE_INTENTION';
        }
        return null;
      };
    },
    replyMetadataType() {
      return metadata => {
        if (metadata?.purchase_intention) {
          return 'PURCHASE_INTENTION';
        }
        return null;
      };
    },
  },
  watch: {
    showHistory(newVal) {
      if (newVal) {
        this.loadHistory();
      }
    },
  },
  methods: {
    loadHistory() {
      this.$store.dispatch('comments/fetchTestModeHistory', {
        pageId: this.post.channel_facebook_page_id,
        postId: this.post.id,
        isFake: true,
        pagination: this.pagination,
      });
    },
    fetchMore() {
      this.pagination.currentPage += 1;
      this.loadHistory();
    },
    closeDrawer() {
      this.$emit('update:showHistory', false);
    },
  },
};
</script>

<template>
  <Transition name="slide">
    <div
      v-if="showHistory"
      :key="testModeHistory.length"
      class="fixed top-0 right-0 w-96 h-full bg-white dark:bg-slate-800 shadow-lg p-4 overflow-y-auto z-50"
    >
      <div class="flex justify-between items-center border-b pb-2">
        <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
          {{ 'Histórico de Testes' }}
        </h3>
        <button
          class="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          @click="closeDrawer"
        >
          <CustomIcon group="general" name="DISMISS" color="#5A6169" />
        </button>
      </div>

      <div class="mt-4 space-y-3">
        <div
          v-for="(comment, index) in testModeHistory.comments"
          :key="index"
          class="p-3 border rounded dark:bg-slate-700"
        >
          <div>
            <div class="flex flex-col w-full gap-2">
              <span
                class="text-sm font-semibold text-slate-900 dark:text-white"
              >
                {{ comment.from_name }}
              </span>
              <div class="flex flex-wrap items-center gap-2">
                <ClassificationBadge
                  v-if="comment.classification"
                  :type="comment.classification"
                />
                <MetadataBadge
                  v-if="comment.metadata?.purchase_intention"
                  :type="commentMetadataType(comment.metadata)"
                />
                <CustomClassificationBadge
                  v-if="
                    Array.isArray(comment.metadata?.custom_classifications) &&
                    comment.metadata.custom_classifications.length
                  "
                  :items="comment.metadata.custom_classifications"
                  :max-visible="2"
                />
              </div>
            </div>
            <p class="text-sm text-slate-800 dark:text-slate-300">
              {{ comment.message }}
            </p>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{ new Date(comment.created_time).toLocaleString() }}
            </p>

            <div class="mt-2">
              <div class="mt-2 space-y-2">
                <div
                  v-for="reply in comment.replies"
                  :key="reply.id"
                  class="ml-4 p-2 border-l-2 border-blue-500"
                >
                  <div class="flex flex-col w-full gap-2 mb-4">
                    <span
                      class="text-sm font-semibold text-slate-900 dark:text-white"
                    >
                      {{ reply.from_name || 'Resposta da IA' }}
                    </span>
                    <div class="flex flex-wrap items-center gap-2">
                      <ClassificationBadge
                        v-if="reply.classification"
                        :type="reply.classification"
                      />
                      <MetadataBadge
                        v-if="reply.metadata?.purchase_intention"
                        :type="replyMetadataType(reply.metadata)"
                      />
                      <CustomClassificationBadge
                        v-if="
                          Array.isArray(
                            reply.metadata?.custom_classifications
                          ) && reply.metadata.custom_classifications.length
                        "
                        :items="reply.metadata.custom_classifications"
                        :max-visible="2"
                      />
                    </div>
                  </div>
                  <p class="text-sm text-slate-800 dark:text-slate-300">
                    {{ reply.message }}
                  </p>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    {{ new Date(reply.created_time).toLocaleString() }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="hasMore" class="mt-4 text-center">
        <button
          class="flex w-full p-3 items-center justify-center bg-woot-75 text-woot-600 rounded-lg mt-6 dark:bg-woot-800 dark:text-woot-100 cursor-pointer hover:bg-woot-100 hover:text-woot-800"
          @click="fetchMore"
        >
          {{ 'Carregar Mais' }}
        </button>
      </div>

      <div
        v-if="!hasMore"
        class="mt-4 text-center text-slate-500 dark:text-slate-400"
      >
        {{ 'Fim do histórico' }}
      </div>
    </div>
  </Transition>
</template>

<style>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease-in-out;
}
.slide-enter,
.slide-leave-to {
  transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease-in-out;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
